import { FunctionComponent, KeyboardEvent, useRef, useState } from "react";

import { faLock, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";

import { Icon } from "@/components/Icon/Icon";

import { BooleanOperator, Chip } from "./types";

interface KeywordChipInputProps {
	operator: BooleanOperator;
	/** Operator badge text, e.g. "AND". */
	label: string;
	/** Short helper under the badge, e.g. "must include". */
	help: string;
	placeholder: string;
	/** Editable chips for this row. */
	chips: Chip[];
	/** Read-only locked chip (e.g. the job-change phrase) rendered after the editable chips. */
	lockedChip?: Chip | null;
	onAdd: (value: string) => void;
	onRemove: (index: number) => void;
	disabled?: boolean;
}

const OPERATOR_BADGE_CLASS: Record<BooleanOperator, string> = {
	and: "bg-blue-action-200 text-blue-action",
	or: "bg-light-blue text-brand-light",
	not: "bg-status-error-light text-status-error",
};

export const KeywordChipInput: FunctionComponent<KeywordChipInputProps> = ({
	operator,
	label,
	help,
	placeholder,
	chips,
	lockedChip,
	onAdd,
	onRemove,
	disabled = false,
}) => {
	const [value, setValue] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (disabled) {
			return;
		}

		if (e.key === "Enter" || e.key === ",") {
			e.preventDefault();

			if (value.trim()) {
				onAdd(value.trim());
				setValue("");
			}
		} else if (e.key === "Backspace" && !value && chips.length > 0) {
			onRemove(chips.length - 1);
		}
	};

	return (
		<div className="grid grid-cols-[88px_1fr] gap-3 items-start">
			<div className="flex flex-col gap-1">
				<span
					className={classNames(
						"inline-flex items-center justify-center h-[38px] rounded-md text-[11px] font-bold tracking-wider",
						OPERATOR_BADGE_CLASS[operator]
					)}
				>
					{label}
				</span>
				<span className="text-[11px] text-center text-text-meta">{help}</span>
			</div>

			<div
				className={classNames(
					"flex flex-wrap gap-1.5 items-center min-h-[38px] rounded-md border border-border-light bg-white px-2 py-1",
					disabled ? "opacity-50 pointer-events-none" : "cursor-text"
				)}
				onClick={() => inputRef.current?.focus()}
			>
				{chips.map((chip, index) => (
					<span
						key={`${operator}-${chip.value}`}
						className="inline-flex items-center gap-1 max-w-full rounded-md pl-2 pr-1 py-1 text-[12px] font-medium bg-light-blue text-blue-action"
					>
						<span className="truncate max-w-[200px]">{chip.value}</span>
						<button
							aria-label={`remove ${chip.value}`}
							className="shrink-0 flex items-center justify-center w-4 h-4 rounded opacity-70 hover:opacity-100 hover:cursor-pointer"
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onRemove(index);
							}}
						>
							<Icon className="text-[10px]" icon={faXmark} />
						</button>
					</span>
				))}

				{lockedChip && (
					<span className="inline-flex items-center gap-1 max-w-full rounded-md pl-2 pr-2 py-1 text-[12px] font-medium bg-status-warning-light text-text-warning">
						<span className="truncate max-w-[200px]">{lockedChip.value}</span>
						<Icon className="shrink-0 text-[10px]" icon={faLock} />
					</span>
				)}

				<input
					ref={inputRef}
					autoComplete="off"
					className="flex-1 min-w-[80px] border-none outline-none bg-transparent text-ssm font-normal py-1"
					disabled={disabled}
					placeholder={placeholder}
					value={value}
					onChange={(e) => setValue(e.target.value)}
					onKeyDown={handleKeyDown}
				/>
			</div>
		</div>
	);
};
